import React from "react";
import { Link, useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { Spinner } from "flowbite-react";
import { ArrowLeft } from "lucide-react";
import { db } from "../firebase";
import AppHeader from "./AppHeader";
import TherapistReviews from "./TherapistReviews";
import { Therapist } from "../types/therapist";
import { scrollWindowToTopSoon } from "./ScrollToTop";
import { acceptsPublicReviews } from "../utils/reviewEligibility";

const getInitials = (name: string): string => {
  if (!name) return "CB";
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .substring(0, 2)
    .toUpperCase();
};

const AvaliarTerapeuta: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [therapist, setTherapist] = React.useState<Therapist | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    scrollWindowToTopSoon();
    const load = async () => {
      if (!id) {
        setError("Link de avaliação inválido.");
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const snapshot = await getDoc(doc(db, "therapists", id));
        if (!snapshot.exists()) {
          setError("Constelador não encontrado.");
          setTherapist(null);
          return;
        }
        const data = snapshot.data() as Omit<Therapist, "id">;
        setTherapist({ id: snapshot.id, ...data });
      } catch (err) {
        console.error("Erro ao carregar constelador para avaliação:", err);
        setError("Não foi possível abrir este link agora.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  return (
    <div className="min-h-screen bg-[#F4F7F6]">
      <AppHeader />
      <main className="mx-auto max-w-3xl px-4 py-8">
        <Link
          to={therapist?.id ? `/terapeuta/${therapist.id}` : "/terapeutas"}
          className="mb-6 inline-flex items-center gap-2 text-sm font-medium text-[#4A7C75] hover:text-[#3A6C65]"
        >
          <ArrowLeft className="h-4 w-4" />
          Voltar ao perfil
        </Link>

        {loading && (
          <div className="flex items-center justify-center py-16">
            <Spinner size="xl" />
          </div>
        )}

        {!loading && error && (
          <div className="rounded-2xl bg-white p-8 text-center shadow-md">
            <p className="text-[#6C4E78]">{error}</p>
          </div>
        )}

        {!loading && therapist && (
          <div className="rounded-2xl bg-white p-6 shadow-md sm:p-8">
            <div className="mb-6 flex items-center gap-4">
              {therapist.profilePhotoURL ? (
                <img
                  src={therapist.profilePhotoURL}
                  alt={therapist.nomePublico}
                  className="h-16 w-16 rounded-full object-cover"
                />
              ) : (
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#4A7C75] text-lg font-semibold text-white">
                  {getInitials(therapist.nomePublico || "")}
                </div>
              )}
              <div>
                <p className="text-sm text-[#85A7A8]">Avaliar constelador</p>
                <h1 className="text-2xl font-semibold text-[#6C4E78]">{therapist.nomePublico}</h1>
                {therapist.title && <p className="text-sm text-[#4A7C75]">{therapist.title}</p>}
              </div>
            </div>
            {acceptsPublicReviews(therapist) && (
              <p className="mb-6 text-sm leading-6 text-[#5A7272]">
                Se você fez uma sessão com este profissional, deixe uma nota e um comentário.
                Não precisa ter agendado pela plataforma.
              </p>
            )}
            <TherapistReviews
              therapistId={therapist.id}
              therapistName={therapist.nomePublico}
              reviewAverage={therapist.reviewAverage || 0}
              reviewCount={therapist.reviewCount || 0}
              acceptsReviews={acceptsPublicReviews(therapist)}
              invite
            />
          </div>
        )}
      </main>
    </div>
  );
};

export default AvaliarTerapeuta;
